// Landing.js — RizzingBot Landing Page
// CSS Module pattern identique à Conversation.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Landing.module.css';

// ─── Sub-components ────────────────────────────────────────────────────────

const RobotHead = ({ variant }) => (
  <div className={styles["wmb-robot-wrap"]}>
    <div className={`${styles["wmb-ear"]} ${styles["l"]}`} />
    <div className={styles["wmb-robot-head"]}>
      <div className={styles["wmb-robot-inner"]}>
        <div className={styles["wmb-eyes"]}>
          <div className={`${styles["wmb-eye"]} ${variant === 'teaser' ? styles["tilted-l"] : ''}`} />
          <div
            className={`${styles["wmb-eye"]} ${variant === 'teaser' ? styles["tilted-r"] : ''}`}
            style={variant === 'observer' ? { width: 13, borderRadius: '4px 4px 2px 2px' } : {}}
          />
        </div>
        <div
          className={styles["wmb-mouth"]}
          style={
            variant === 'provoker'
              ? { borderRadius: '6px 6px 0 0', borderBottom: 'none', borderTop: '1.5px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)' }
              : variant === 'teaser'
              ? { width: 14, borderRadius: 3 }
              : variant === 'direct'
              ? { width: 22, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.5)', border: 'none' }
              : {}
          }
        />
      </div>
    </div>
    <div className={`${styles["wmb-ear"]} ${styles["r"]}`} />
  </div>
);

const TypingDots = ({ delays = [0, 0.2, 0.4] }) => (
  <div className={styles["wmb-typing"]}>
    {delays.map((d, i) => (
      <div key={i} className={styles["wmb-dot"]} style={{ animationDelay: `${d}s` }} />
    ))}
  </div>
);

const WingmanBot = ({ variant, accentClass, name, energy, conf, confLabel, confPct, ctx, suggestions, tone }) => (
  <div className={`${styles["wmb"]} ${styles[`wmb-${accentClass}`]}`}>
    <div className={styles["wmb-mascot-zone"]}>
      <RobotHead variant={variant} />
      <div className={styles["wmb-bot-id"]}>
        <div className={styles["wmb-bot-name"]}>{name}</div>
        <div className={styles["wmb-bot-energy"]}>{energy}</div>
        <div className={styles["wmb-conf-row"]}>
          <div className={styles["wmb-conf-track"]}>
            <div className={styles["wmb-conf-fill"]} style={{ width: confPct }} />
          </div>
          <span className={styles["wmb-conf"]}>{conf}</span>
        </div>
        <div className={styles["wmb-conf-label-txt"]}>{confLabel}</div>
      </div>
    </div>

    <div className={styles["wmb-chat"]}>
      <div className={styles["wmb-ctx-you"]}>{ctx.label}</div>
      <div className={styles["wmb-you"]}>{ctx.message}</div>
      <TypingDots delays={ctx.delays} />
      {suggestions.map((sug, i) => (
        <div key={i} className={styles["wmb-sug"]}>
          <span className={styles["wmb-num"]}>{i + 1}</span>
          {sug}
        </div>
      ))}
    </div>

    <div className={styles["wmb-foot"]}>
      <span className={styles["wmb-foot-icon"]}>{ctx.icon}</span>
      <span className={styles["wmb-tone-chip"]}>{tone}</span>
    </div>
  </div>
);

const StarRating = () => (
  <div className={styles["t-stars"]}>
    {[...Array(5)].map((_, i) => (
      <span key={i} className={styles["t-star"]}>★</span>
    ))}
  </div>
);

// ─── Inline SVG icons ──────────────────────────────────────────────────────
const ArrowIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);
const LockIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

// ─── Main Component ────────────────────────────────────────────────────────

const Landing = () => {
  const { t } = useTranslation('landing');
  const navigate = useNavigate();

  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Prices: [monthly, annual]
  const PRICES = {
    starter: [4.99, 3.99],
    premium: [9.99, 7.99],
    pro:     [19.99, 15.99],
  };
 
 
  // Scroll reveal — utilise data-reveal pour éviter conflit avec CSS Module
useEffect(() => {
  const elements = Array.from(document.getElementsByClassName(styles.reveal));
 
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add(styles.in);
        io.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
  });

  elements.forEach(el => io.observe(el));

  return () => io.disconnect();
}, []);

  const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);
  const flipBill  = () => setAnnual(a => !a);

  const handleCTA   = () => navigate('/signup');
  const handleLogin = () => navigate('/login');

  // ── Wingman bots data ────────────────────────────────────────────────────
  const bots = [
    {
      variant: 'provoker', accentClass: 'rage',
      name: t('wingmen.provoker_name'),
      energy: t('wingmen.provoker_energy'),
      conf: t('wingmen.provoker_conf'),
      confLabel: t('wingmen.provoker_conf_label'),
      confPct: '92%',
      ctx: { label: t('wingmen.you_go_first'), message: t('wingmen.provoker_ctx'), icon: '🔥', delays: [0, 0.2, 0.4] },
      suggestions: [t('wingmen.provoker_sug1'), t('wingmen.provoker_sug2'), t('wingmen.provoker_sug3')],
      tone: t('wingmen.provoker_tone'),
    },
    {
      variant: 'charmer', accentClass: 'charm',
      name: t('wingmen.charmer_name'),
      energy: t('wingmen.charmer_energy'),
      conf: t('wingmen.charmer_conf'),
      confLabel: t('wingmen.charmer_conf_label'),
      confPct: '88%',
      ctx: { label: t('wingmen.you_go_first'), message: t('wingmen.charmer_ctx'), icon: '😄', delays: [0.1, 0.3, 0.5] },
      suggestions: [t('wingmen.charmer_sug1'), t('wingmen.charmer_sug2'), t('wingmen.charmer_sug3')],
      tone: t('wingmen.charmer_tone'),
    },
    {
      variant: 'teaser', accentClass: 'tease',
      name: t('wingmen.teaser_name'),
      energy: t('wingmen.teaser_energy'),
      conf: t('wingmen.teaser_conf'),
      confLabel: t('wingmen.teaser_conf_label'),
      confPct: '85%',
      ctx: { label: t('wingmen.you_go_first'), message: t('wingmen.teaser_ctx'), icon: '😏', delays: [0.2, 0.4, 0.6] },
      suggestions: [t('wingmen.teaser_sug1'), t('wingmen.teaser_sug2'), t('wingmen.teaser_sug3')],
      tone: t('wingmen.teaser_tone'),
    },
    {
      variant: 'observer', accentClass: 'obs',
      name: t('wingmen.observer_name'),
      energy: t('wingmen.observer_energy'),
      conf: t('wingmen.observer_conf'),
      confLabel: t('wingmen.observer_conf_label'),
      confPct: '90%',
      ctx: { label: t('wingmen.you_go_first'), message: t('wingmen.observer_ctx'), icon: '🔍', delays: [0.15, 0.35, 0.55] },
      suggestions: [t('wingmen.observer_sug1'), t('wingmen.observer_sug2'), t('wingmen.observer_sug3')],
      tone: t('wingmen.observer_tone'),
    },
    {
      variant: 'direct', accentClass: 'direct',
      name: t('wingmen.direct_name'),
      energy: t('wingmen.direct_energy'),
      conf: t('wingmen.direct_conf'),
      confLabel: t('wingmen.direct_conf_label'),
      confPct: '82%',
      ctx: { label: t('wingmen.you_go_first'), message: t('wingmen.direct_ctx'), icon: '⚡', delays: [0.25, 0.45, 0.65] },
      suggestions: [t('wingmen.direct_sug1'), t('wingmen.direct_sug2'), t('wingmen.direct_sug3')],
      tone: t('wingmen.direct_tone'),
    },
  ];

  // ── FAQ items ────────────────────────────────────────────────────────────
  const faqs = [
    { q: t('faq.q1'), a: t('faq.a1') },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
    { q: t('faq.q5'), a: t('faq.a5') },
    { q: t('faq.q6'), a: t('faq.a6') },
  ];

  // ── Testimonials ─────────────────────────────────────────────────────────
  const testimonials = [
    { text: t('testimonials.t1_text'), name: t('testimonials.t1_name'), city: t('testimonials.t1_city'), plan: 'Premium', avatar: 'M', grad: '' },
    { text: t('testimonials.t2_text'), name: t('testimonials.t2_name'), city: t('testimonials.t2_city'), plan: 'Starter', avatar: 'L', grad: 'linear-gradient(135deg,#1d4ed8,#6366f1)' },
    { text: t('testimonials.t3_text'), name: t('testimonials.t3_name'), city: t('testimonials.t3_city'), plan: 'Premium', avatar: 'T', grad: 'linear-gradient(135deg,#2563eb,#4f46e5)' },
    { text: t('testimonials.t4_text'), name: t('testimonials.t4_name'), city: t('testimonials.t4_city'), plan: 'Pro',     avatar: 'A', grad: 'linear-gradient(135deg,#1e40af,#6366f1)' },
    { text: t('testimonials.t5_text'), name: t('testimonials.t5_name'), city: t('testimonials.t5_city'), plan: 'Starter', avatar: 'R', grad: 'linear-gradient(135deg,#2563eb,#3730a3)' },
    { text: t('testimonials.t6_text'), name: t('testimonials.t6_name'), city: t('testimonials.t6_city'), plan: 'Premium', avatar: 'K', grad: 'linear-gradient(135deg,#1d4ed8,#4338ca)' },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={styles["landing-root"]}>
      <div className={styles["global-blob"]} />

      {/* ── NAV ── */}
      <nav className={styles["landing-nav"]}>
        <button onClick={() => window.scrollTo(0, 0)} className={styles["logo"]}>
          <img src="/logo512.png" alt="RizzingBot" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          RizzingBot
        </button>
        <div className={styles["nav-center"]}>
          <a href="#how">{t('nav.how')}</a>
          <a href="#icebreakers">{t('nav.wingmen')}</a>
          <a href="#features">{t('nav.features')}</a>
          <a href="#pricing">{t('nav.pricing')}</a>
          <a href="#faq">{t('nav.faq')}</a>
        </div>
        <div className={styles["nav-right"]}>
          <button className={styles["nav-login"]} onClick={handleLogin}>{t('nav.sign_in')}</button>
          <button className={styles["btn-cta"]} onClick={handleCTA}>
            {t('nav.get_started')}
            <ArrowIcon />
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className={styles["hero"]}>
        <div className={styles["hero-beam"]} />
        <div className={styles["eyebrow"]}>
          <div className={styles["eyebrow-dot"]} />
          {t('hero.eyebrow')}
        </div>
        <h1>
          {t('hero.h1_line1')}<br />{t('hero.h1_line2')}
        </h1>
        <p className={styles["hero-sub"]}>{t('hero.sub')}</p>
        <div className={styles["hero-actions"]}>
          <button className={styles["btn-cta"]} onClick={handleCTA}>
            {t('hero.cta_primary')} <ArrowIcon />
          </button>
          <a href="#how" className={styles["btn-outline"]}>{t('hero.cta_secondary')}</a>
        </div>
        <div className={styles["hero-trust"]}>
          <span><div className={styles["trust-check"]}>✓</div>{t('hero.trust_credits')}</span>
          <div className={styles["trust-sep"]} />
          <span><div className={styles["trust-check"]}>✓</div>{t('hero.trust_data')}</span>
          <div className={styles["trust-sep"]} />
          <span><div className={styles["trust-check"]}>✓</div>{t('hero.trust_cancel')}</span>
          <div className={styles["trust-sep"]} />
          <span><div className={styles["trust-check"]}>✓</div>{t('hero.trust_gdpr')}</span>
        </div>

        {/* Dashboard Mockup */}
        <div className={styles["dash-wrap"]}>
          <div className={styles["dash-shell"]}>
            <div className={styles["dash-titlebar"]}>
              <div className={styles["win-btns"]}>
                <div className={`${styles["win-btn"]} ${styles["r"]}`} />
                <div className={`${styles["win-btn"]} ${styles["y"]}`} />
                <div className={`${styles["win-btn"]} ${styles["g"]}`} />
              </div>
              <div className={styles["dash-tabs"]}>
                <div className={`${styles["dash-tab"]} ${styles["active"]}`}>{t('mockup.tab_conversations')}</div>
                <div className={styles["dash-tab"]}>{t('mockup.tab_analytics')}</div>
                <div className={styles["dash-tab"]}>{t('mockup.tab_planner')}</div>
              </div>
              <div className={styles["dash-url"]}>app.rizzingbot.com</div>
            </div>

            <div className={styles["dash-body"]}>
              {/* Sidebar */}
              <div className={styles["dash-sidebar"]}>
                <div className={styles["sidebar-brand"]}>
                  <img src="/logo1.png" alt="RizzingBot" style={{ width: 20, height: 20, objectFit: 'contain' }} />RizzingBot
                </div>
                <div className={styles["sb-section"]}>{t('mockup.sidebar_active')}</div>
                <div className={`${styles["sb-item"]} ${styles["active"]}`}>
                  <span className={styles["sb-ic"]}>💬</span> Léa, 24
                  <span className={styles["sb-badge"]}>LIVE</span>
                </div>
                <div className={styles["sb-item"]}><span className={styles["sb-ic"]}>💬</span> Camille, 27</div>
                <div className={styles["sb-item"]}><span className={styles["sb-ic"]}>💬</span> Sophie, 23</div>
                <div className={styles["sb-section"]}>{t('mockup.sidebar_tools')}</div>
                <div className={styles["sb-item"]}><span className={styles["sb-ic"]}>🧊</span>{t('mockup.sidebar_ice')}</div>
                <div className={styles["sb-item"]}><span className={styles["sb-ic"]}>📅</span>{t('mockup.sidebar_planner')}</div>
                <div className={styles["sb-item"]}><span className={styles["sb-ic"]}>📊</span>{t('mockup.sidebar_analytics')}</div>
                <div className={styles["sb-item"]}><span className={styles["sb-ic"]}>⚙️</span>{t('mockup.sidebar_settings')}</div>
              </div>

              {/* Chat */}
              <div className={styles["dash-chat"]}>
                <div className={styles["chat-topbar"]}>
                  <div className={styles["conv-ava"]}>L</div>
                  <div>
                    <div className={styles["conv-name"]}>Léa, 24 — Instagram DM</div>
                    <div className={styles["conv-sub"]}>{t('mockup.conv_sub')}</div>
                  </div>
                  <div className={styles["conv-badge"]}>{t('mockup.wingman_active')}</div>
                </div>
                <div className={styles["chat-msgs"]}>
                  <div className={`${styles["msg-bubble"]} ${styles["msg-her"]}`}>{t('mockup.chat_her_1')}</div>
                  <div className={`${styles["msg-bubble"]} ${styles["msg-me"]}`}>{t('mockup.chat_me')}</div>
                  <div className={`${styles["msg-bubble"]} ${styles["msg-her"]}`}>{t('mockup.chat_her_2')}</div>
                  <div className={`${styles["msg-bubble"]} ${styles["msg-ai"]}`}>{t('mockup.chat_ai')}</div>
                </div>
                <div className={styles["chat-input-row"]}>
                  {t('mockup.chat_placeholder')}
                  <button className={styles["input-send"]}>↑</button>
                </div>
              </div>

              {/* AI Panel */}
              <div className={styles["dash-ai"]}>
                <div className={styles["ai-topbar"]}>
                  <span className={styles["ai-topbar-label"]}>{t('mockup.ai_label')}</span>
                  <span className={styles["ai-status"]}>{t('mockup.ai_status')}</span>
                </div>
                <div className={styles["ai-body"]}>
                  <div className={styles["ai-stat-row"]}>
                    <div className={styles["ai-stat"]}>
                      <div className={styles["ai-stat-label"]}>{t('mockup.ai_interest')}</div>
                      <div className={`${styles["ai-stat-value"]} ${styles["green"]}`}>82%</div>
                      <div className={styles["ai-stat-sub"]}>{t('mockup.ai_high')}</div>
                    </div>
                    <div className={styles["ai-stat"]}>
                      <div className={styles["ai-stat-label"]}>{t('mockup.ai_ghost')}</div>
                      <div className={`${styles["ai-stat-value"]} ${styles["blue"]}`}>8%</div>
                      <div className={styles["ai-stat-sub"]}>{t('mockup.ai_low')}</div>
                    </div>
                  </div>
                  <div className={styles["score-section"]}>
                    {[
                      { label: t('mockup.ai_interest_level'), cls: 'blue',   w: '82%' },
                      { label: t('mockup.ai_energy'),         cls: 'indigo', w: '75%' },
                      { label: t('mockup.ai_date_ready'),     cls: 'green',  w: '88%' },
                    ].map(({ label, cls, w }) => (
                      <div key={label} className={styles["score-row"]}>
                        <div className={styles["score-label"]}>{label}</div>
                        <div className={styles["score-bar"]}>
                          <div className={`${styles["score-fill"]} ${styles[cls]}`} style={{ width: w }} />
                        </div>
                        <div className={styles["score-pct"]}>{w}</div>
                      </div>
                    ))}
                  </div>
                  <div className={styles["sug-section"]}>
                    <div className={styles["sug-header"]}>{t('mockup.ai_suggestions')}</div>
                    <div className={`${styles["sug-option"]} ${styles["sel"]}`}>
                      "{t('mockup.ai_sug_1')}"<span className={styles["conf"]}>93%</span>
                    </div>
                    <div className={styles["sug-option"]}>
                      "{t('mockup.ai_sug_2')}"<span className={styles["conf"]}>81%</span>
                    </div>
                    <button className={styles["send-sug"]}>{t('mockup.ai_use')}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Platforms */}
        <div className={styles["platform-row"]}>
          <span className={styles["plat-label"]}>{t('platforms.label')}</span>
          {['Tinder','Bumble','Hinge','Instagram','Snapchat','TikTok'].map(p => (
            <span key={p} className={styles["plat-chip"]}>{p}</span>
          ))}
          <span className={styles["plat-chip"]}>{t('platforms.more')}</span>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className={styles["stats-bar"]}>
        <div className={styles["stats-inner"]}>
          {[
            { val: t('stats.users_val'),  label: t('stats.users_label') },
            { val: t('stats.reply_val'),  label: t('stats.reply_label') },
            { val: t('stats.dates_val'),  label: t('stats.dates_label') },
            { val: t('stats.rating_val'), label: t('stats.rating_label') },
          ].map(({ val, label }) => (
            <div key={label} className={styles["stat-block"]}>
              <div className={styles["stat-big"]}>{val}</div>
              <div className={styles["stat-desc"]}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <section className={styles["sec"]} id="how">
        <div className={styles["wrap"]}>
          <div className={styles["reveal"]}>
            <div className={styles["sec-tag"]}>{t('how.tag')}</div>
            <h2 className={styles["sec-h2"]}>{t('how.h2_line1')}<br />{t('how.h2_line2')}</h2>
            <p className={styles["sec-p"]}>{t('how.sub')}</p>
          </div>
          <div className={`${styles["timeline"]} ${styles["reveal"]}`} style={{ transitionDelay: '0.1s' }}>
            {[
              { num: '01', icon: '💬', title: t('how.step1_title'), desc: t('how.step1_desc'), arr: true },
              { num: '02', icon: '🧠', title: t('how.step2_title'), desc: t('how.step2_desc'), arr: true },
              { num: '03', icon: '📅', title: t('how.step3_title'), desc: t('how.step3_desc'), arr: false },
            ].map(({ num, icon, title, desc, arr }) => (
              <div key={num} className={styles["timeline-step"]}>
                <div className={styles["step-num-badge"]}>{num}</div>
                <span className={styles["step-icon"]}>{icon}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
                {arr && <div className={styles["step-arr"]} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WINGMAN BOTS ── */}
      <section className={`${styles["sec"]} ${styles["ib-sec"]}`} id="icebreakers">
        <div className={styles["wrap"]}>
          <div className={styles["reveal"]}>
            <div className={styles["sec-tag"]}>{t('wingmen.tag')}</div>
            <h2 className={styles["sec-h2"]}>{t('wingmen.h2_line1')}<br />{t('wingmen.h2_line2')}</h2>
            <p className={styles["sec-p"]}>{t('wingmen.sub')}</p>
          </div>
          <div className={`${styles["wm-grid-v6"]} ${styles["reveal"]}`} style={{ transitionDelay: '0.1s' }}>
            {bots.map((bot) => (
              <WingmanBot key={bot.variant} {...bot} />
            ))}
          </div>
          <div className={`${styles["reveal"]} ${styles["intel-note"]}`} style={{ transitionDelay: '0.22s' }}>
            <div className={styles["intel-icon"]}>🧠</div>
            <div>
              <div className={styles["intel-title"]}>{t('wingmen.intelligence_title')}</div>
              <div className={styles["intel-desc"]}>{t('wingmen.intelligence_desc')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className={`${styles["sec"]} ${styles["sec-alt"]}`} id="features">
        <div className={styles["wrap"]}>
          <div className={styles["reveal"]}>
            <div className={styles["sec-tag"]}>{t('features.tag')}</div>
            <h2 className={styles["sec-h2"]}>{t('features.h2_line1')}<br />{t('features.h2_line2')}</h2>
            <p className={styles["sec-p"]}>{t('features.sub')}</p>
          </div>
          <div className={`${styles["bento"]} ${styles["reveal"]}`} style={{ transitionDelay: '0.1s' }}>

            {/* Sentiment Analysis — wide */}
            <div className={`${styles["bento-card"]} ${styles["wide"]} ${styles["sentiment-upgrade"]}`}>
              <div className={styles["sa-shell"]}>
                <div className={styles["sa-left"]}>
                  <div className={styles["sa-header-row"]}>
                    <div className={styles["bento-icon"]} style={{ marginBottom: 0, flexShrink: 0 }}>📊</div>
                    <div>
                      <h3 style={{ marginBottom: 3 }}>{t('features.sa_title')}</h3>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{t('features.sa_desc')}</p>
                    </div>
                    <span
                      className={`${styles["feature-tag"]} ${styles["tag-blue"]}`}
                      style={{ marginTop: 0, marginLeft: 'auto', alignSelf: 'flex-start' }}
                    >
                      {t('features.sa_tag')}
                    </span>
                  </div>
                  <div className={styles["sa-incoming"]}>
                    <div className={styles["sa-incoming-label"]}>{t('features.sa_her_label')}</div>
                    <div className={styles["sa-incoming-msg"]}>{t('features.sa_her_msg')}</div>
                  </div>
                  <div className={styles["sa-scores"]}>
                    <div className={styles["sa-score-box"]}>
                      <div className={styles["sa-score-title"]}>{t('features.sa_interest_title')}</div>
                      <div className={`${styles["sa-score-num"]} ${styles["blue"]}`}>
                        71<span style={{ fontSize: 16, fontWeight: 600 }}>%</span>
                      </div>
                      <div className={styles["sa-score-sub"]}>{t('features.sa_interest_sub')}</div>
                    </div>
                    <div className={styles["sa-score-box"]}>
                      <div className={styles["sa-score-title"]}>{t('features.sa_ghost_title')}</div>
                      <div className={`${styles["sa-score-num"]} ${styles["green"]}`}>
                        24<span style={{ fontSize: 16, fontWeight: 600 }}>%</span>
                      </div>
                      <div className={styles["sa-score-sub"]}>{t('features.sa_ghost_sub')}</div>
                    </div>
                  </div>
                  <div className={styles["sa-reasoning"]}>
                    <div className={styles["sa-reasoning-title"]}>{t('features.sa_why')}</div>
                    {[
                      { icon: 'pos', text: t('features.sa_signal_1_pos'), bold: t('features.sa_signal_1_bold') },
                      { icon: 'pos', text: t('features.sa_signal_2_pos'), bold: t('features.sa_signal_2_bold') },
                      { icon: 'pos', text: t('features.sa_signal_3_pos'), bold: t('features.sa_signal_3_bold') },
                      { icon: 'neg', text: t('features.sa_signal_4_neg'), bold: t('features.sa_signal_4_bold') },
                    ].map(({ icon, text, bold }, i) => (
                      <div key={i} className={styles["sa-signal"]}>
                        <span className={`${styles["sa-signal-icon"]} ${styles[icon]}`}>
                          {icon === 'pos' ? '✓' : '✗'}
                        </span>
                        <span className={styles["sa-signal-text"]}>{text} <strong>{bold}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles["sa-right"]}>
                  <div className={styles["sa-panel-label"]}>{t('features.sa_live')}</div>
                  <div className={styles["sa-bars-block"]}>
                    {[
                      { name: t('features.sa_bar_interest'), cls: 'b', w: '71%' },
                      { name: t('features.sa_bar_energy'),   cls: 'i', w: '64%' },
                      { name: t('features.sa_bar_date'),     cls: 'g', w: '48%' },
                      { name: t('features.sa_bar_ghost'),    cls: 'g', w: '24%' },
                    ].map(({ name, cls, w }) => (
                      <div key={name} className={styles["sa-bar-row"]}>
                        <div className={styles["sa-bar-name"]}>{name}</div>
                        <div className={styles["sa-bar-track"]}>
                          <div className={`${styles["sa-bar-fill"]} ${styles[cls]}`} style={{ width: w }} />
                        </div>
                        <div className={styles["sa-bar-pct"]}>{w}</div>
                      </div>
                    ))}
                  </div>
                  <div className={styles["sa-rec"]}>
                    <div className={styles["sa-rec-label"]}>{t('features.sa_rec_label')}</div>
                    <div className={styles["sa-rec-text"]}>
                      {t('features.sa_rec_text')} <em>{t('features.sa_rec_next_week')}</em> {t('features.sa_rec_text2')}
                    </div>
                  </div>
                  <div className={styles["sa-timing"]}>
                    <div className={styles["sa-timing-icon"]}>⏱</div>
                    <div>
                      <strong>{t('features.sa_timing_title')}</strong>
                      <span>{t('features.sa_timing_desc')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Simple bento cards */}
            {[
              { icon: '🤖', title: t('features.wingman_title'), desc: t('features.wingman_desc'),  tag: t('features.wingman_tag'), tagCls: 'tag-indigo' },
              { icon: '🛡️', title: t('features.anti_title'),   desc: t('features.anti_desc'),     tag: t('features.anti_tag'),    tagCls: 'tag-blue'   },
              { icon: '❄️', title: t('features.ice_title'),    desc: t('features.ice_desc'),      tag: t('features.ice_tag'),     tagCls: 'tag-green'  },
              { icon: '✍️', title: t('features.reform_title'), desc: t('features.reform_desc'),   tag: t('features.reform_tag'),  tagCls: 'tag-amber'  },
            ].map(({ icon, title, desc, tag, tagCls }) => (
              <div key={title} className={styles["bento-card"]}>
                <div className={styles["bento-icon"]}>{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <span className={`${styles["feature-tag"]} ${styles[tagCls]}`}>{tag}</span>
              </div>
            ))}

            {/* Date Planner — wide */}
            <div className={`${styles["bento-card"]} ${styles["wide"]}`}>
              <div className={styles["bento-icon"]}>📅</div>
              <h3>{t('features.date_title')}</h3>
              <p>{t('features.date_desc')}</p>
              <span className={`${styles["feature-tag"]} ${styles["tag-indigo"]}`}>{t('features.date_tag')}</span>
              <div className={styles["date-options-row"]}>
                {[
                  { cls: 'opt-a', pct: '89%', label: 'OPTION A', text: t('features.date_opt_a') },
                  { cls: 'opt-b', pct: '74%', label: 'OPTION B', text: t('features.date_opt_b') },
                  { cls: 'opt-c', pct: '68%', label: 'OPTION C', text: t('features.date_opt_c') },
                ].map(({ cls, pct, label, text }) => (
                  <div key={cls} className={`${styles["date-opt"]} ${styles[cls]}`}>
                    <div className={styles["date-opt-label"]}>{label} · {pct}</div>
                    <div className={styles["date-opt-text"]}>{text}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className={styles["sec"]} id="pricing">
        <div className={styles["wrap"]}>
          <div style={{ textAlign: 'center' }} className={styles["reveal"]}>
            <div className={styles["sec-tag"]} style={{ justifyContent: 'center' }}>{t('pricing.tag')}</div>
            <h2 className={styles["sec-h2"]}>{t('pricing.h2')}</h2>
            <p className={styles["sec-p"]} style={{ margin: '0 auto' }}>{t('pricing.sub')}</p>
            <div className={styles["bill-toggle-row"]}>
              <span className={`${styles["bill-label"]} ${!annual ? styles["active-label"] : ''}`}>
                {t('pricing.monthly')}
              </span>
            {/* ── PRICING ── 
              <div
                className={`${styles["tog"]} ${annual ? styles["on"] : ''}`}
                onClick={flipBill}
              >
                <div className={styles["tog-knob"]} />
              </div>
            */}
              <span className={`${styles["bill-label"]} ${annual ? styles["active-label"] : ''}`}>
                {t('pricing.yearly')}
              </span>
              <span className={styles["save-tag"]}>{t('pricing.save')}</span>
            </div>
          </div>

          <div className={`${styles["pricing-grid"]} ${styles["reveal"]}`} style={{ transitionDelay: '0.12s' }}>
            {/* FREE */}
            <div className={styles["p-card"]}>
              <div className={styles["p-tier"]}>{t('pricing.free_tier')}</div>
              <div className={styles["p-price"]}>
                <sup>$</sup>0<span className={styles["per"]}> {t('pricing.per_mo')}</span>
              </div>
              <div className={styles["p-credits"]}>{t('pricing.free_credits')}</div>
              <ul className={styles["p-feats"]}>
                {['free_f1','free_f2','free_f3','free_f4','free_f5'].map(k => (
                  <li key={k}><span className={styles["ck"]}>✓</span>{t(`pricing.${k}`)}</li>
                ))}
              </ul>
              <button className={styles["p-btn"]} onClick={handleCTA}>{t('pricing.free_cta')}</button>
            </div>

            {/* STARTER */}
            <div className={styles["p-card"]}>
              <div className={styles["p-tier"]}>{t('pricing.starter_tier')}</div>
              <div className={styles["p-price"]}>
                <sup>$</sup>{PRICES.starter[annual ? 1 : 0].toFixed(2)}
                <span className={styles["per"]}> {t('pricing.per_mo')}</span>
              </div>
              <div className={styles["p-credits"]}>{t('pricing.starter_credits')}</div>
              <ul className={styles["p-feats"]}>
                {['starter_f1','starter_f2','starter_f3','starter_f4','starter_f5','starter_f6','starter_f7'].map(k => (
                  <li key={k}><span className={styles["ck"]}>✓</span>{t(`pricing.${k}`)}</li>
                ))}
              </ul>
              <button className={styles["p-btn"]} onClick={handleCTA}>{t('pricing.starter_cta')}</button>
            </div>

            {/* PREMIUM — featured */}
            <div className={`${styles["p-card"]} ${styles["featured"]}`}>
              <div className={styles["p-popular"]}>{t('pricing.popular')}</div>
              <div className={styles["p-tier"]} style={{ color: '#93c5fd' }}>{t('pricing.premium_tier')}</div>
              <div className={styles["p-price"]}>
                <sup>$</sup>{PRICES.premium[annual ? 1 : 0].toFixed(2)}
                <span className={styles["per"]}> {t('pricing.per_mo')}</span>
              </div>
              <div className={styles["p-credits"]}>{t('pricing.premium_credits')}</div>
              <ul className={styles["p-feats"]}>
                {['premium_f1','premium_f2','premium_f3','premium_f4','premium_f5','premium_f6','premium_f7','premium_f8'].map(k => (
                  <li key={k}><span className={styles["ck"]}>✓</span>{t(`pricing.${k}`)}</li>
                ))}
              </ul>
              <button className={`${styles["p-btn"]} ${styles["cta-btn"]}`} onClick={handleCTA}>
                {t('pricing.premium_cta')}
              </button>
            </div>

            {/* PRO */}
            <div className={styles["p-card"]}>
              <div className={styles["p-tier"]} style={{ color: '#fcd34d' }}>{t('pricing.pro_tier')}</div>
              <div className={styles["p-price"]}>
                <sup>$</sup>{PRICES.pro[annual ? 1 : 0].toFixed(2)}
                <span className={styles["per"]}> {t('pricing.per_mo')}</span>
              </div>
              <div className={styles["p-credits"]} style={{ color: '#fcd34d' }}>{t('pricing.pro_credits')}</div>
              <ul className={styles["p-feats"]}>
                {['pro_f1','pro_f2','pro_f3','pro_f4','pro_f5','pro_f6','pro_f7'].map(k => (
                  <li key={k}><span className={styles["ck"]}>✓</span>{t(`pricing.${k}`)}</li>
                ))}
              </ul>
              <button
                className={styles["p-btn"]}
                style={{ borderColor: 'rgba(252,211,77,0.25)', color: '#fcd34d' }}
                onClick={handleCTA}
              >
                {t('pricing.pro_cta')}
              </button>
            </div>
          </div>

          {/* Trust row */}
          <div className={`${styles["trust-row"]} ${styles["reveal"]}`}>
            {[
              { icon: <LockIcon />,   label: t('pricing.trust_stripe') },
              { icon: <CheckIcon />,  label: t('pricing.trust_cancel') },
              { icon: <ShieldIcon />, label: t('pricing.trust_money_back') },
              { icon: <ClockIcon />,  label: t('pricing.trust_credits') },
            ].map(({ icon, label }) => (
              <span key={label} className={styles["trust-item"]}>{icon}{label}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className={`${styles["sec"]} ${styles["sec-alt"]}`}>
        <div className={styles["wrap"]}>
          <div style={{ textAlign: 'center' }} className={styles["reveal"]}>
            <div className={styles["sec-tag"]} style={{ justifyContent: 'center' }}>{t('testimonials.tag')}</div>
            <h2 className={styles["sec-h2"]}>{t('testimonials.h2')}</h2>
          </div>
          <div className={styles["testi-grid"]}>
            {testimonials.map(({ text, name, city, plan, avatar, grad }, i) => (
              <div
                key={i}
                className={`${styles["t-card"]} ${styles["reveal"]}`}
                style={{ transitionDelay: `${0.05 * (i % 3)}s` }}
              >
                <StarRating />
                <p className={styles["t-text"]}>{text}</p>
                <div className={styles["t-author"]}>
                  <div className={styles["t-ava"]} style={grad ? { background: grad } : {}}>{avatar}</div>
                  <div>
                    <div className={styles["t-name"]}>{name}</div>
                    <div className={styles["t-meta"]}>{city}</div>
                  </div>
                  <div className={styles["t-plan"]}>{plan}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className={styles["sec"]} id="faq">
        <div className={styles["wrap"]}>
          <div style={{ textAlign: 'center' }} className={styles["reveal"]}>
            <div className={styles["sec-tag"]} style={{ justifyContent: 'center' }}>{t('faq.tag')}</div>
            <h2 className={styles["sec-h2"]}>{t('faq.h2')}</h2>
          </div>
          <div className={`${styles["faq-col"]} ${styles["reveal"]}`} style={{ transitionDelay: '0.1s' }}>
            {faqs.map(({ q, a }, i) => (
              <div
                key={i}
                className={`${styles["faq-item"]} ${openFaq === i ? styles["open"] : ''}`}
              >
                <button className={styles["faq-trigger"]} onClick={() => toggleFaq(i)}>
                  {q}
                  <span className={styles["faq-chevron"]}>{openFaq === i ? '−' : '+'}</span>
                </button>
                <div className={styles["faq-ans"]}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles["cta-sec"]}>
        <div className={styles["cta-bg-glow"]} />
        <div style={{ position: 'relative', zIndex: 1 }} className={styles["reveal"]}>
          <h2>
            {t('cta.h2_line1')}<br />
            <span className={styles["gradient-text"]}>{t('cta.h2_gradient')}</span>
          </h2>
          <p>{t('cta.sub')}</p>
          <button
            className={styles["btn-cta"]}
            style={{ fontSize: 15, padding: '14px 32px' }}
            onClick={handleCTA}
          >
            {t('cta.button')} <ArrowIcon />
          </button>
          <p className={styles["cta-footnote"]}>{t('cta.footnote')}</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className={styles["landing-footer"]}>
        <button onClick={() => window.scrollTo(0, 0)} className={styles["foot-logo"]}>
          <img src="/logo1.png" alt="RizzingBot" style={{ width: 28, height: 28, objectFit: 'contain' }} />RizzingBot
        </button>
        <div className={styles["foot-links"]}>
          {[
            { k: 'privacy',    h: '/privacy' },
            { k: 'terms',      h: '/terms' },
            { k: 'blog',       h: '/blog' },
            { k: 'support',    h: '/support' },
            { k: 'affiliates', h: '/affiliates/login' },
          ].map(({ k, h }) => (
            <a key={k} href={h}>{t(`footer.${k}`)}</a>
          ))}
        </div>
        <div className={styles["foot-copy"]}>{t('footer.copy')}</div>
      </footer>
    </div>
  );
};

export default Landing;